import {
  RfpCriterionResult,
  RfpIndividualResults,
  RfpIndividualResultsInput,
  RfpProviderScore,
  RfpResultRow,
  RfpSubCriterionResult,
  RfpTotalResult,
} from './rfp-individual-results.types';

export const HIGHLIGHT_COUNT = 3;

export function buildRfpIndividualResults(input: RfpIndividualResultsInput): RfpIndividualResults {
  const criteria = input.rows.map((row) => criterionResult(row, input));
  const scored = criteria
    .flatMap((criterion) => criterion.subCriteria)
    .filter((subCriterion) => subCriterion.percentage !== null)
    .sort((first, second) => (second.percentage ?? 0) - (first.percentage ?? 0));
  const highlightCount = Math.min(HIGHLIGHT_COUNT, Math.floor(scored.length / 2));

  return {
    total: totalResult(input),
    criteria,
    strengths: scored.slice(0, highlightCount),
    weaknesses: scored.slice(scored.length - highlightCount).reverse(),
  };
}

export function rankOf(
  totals: Map<string, RfpProviderScore>,
  providerIds: string[],
  providerId: string,
): number | null {
  const own = totals.get(providerId);
  if (!own) {
    return null;
  }
  return providerIds.filter((id) => (totals.get(id)?.percentage ?? -1) > own.percentage).length + 1;
}

function totalResult(input: RfpIndividualResultsInput): RfpTotalResult | null {
  const own = input.totals.get(input.providerId);
  const rank = rankOf(input.totals, input.providerIds, input.providerId);
  if (!own || rank === null) {
    return null;
  }
  const scoredProviders = input.providerIds.filter((id) => input.totals.has(id)).length;
  return {
    value: own.value,
    maxValue: own.percentage > 0 ? own.value / (own.percentage / 100) : 0,
    percentage: own.percentage,
    rank,
    providerCount: scoredProviders,
  };
}

function criterionResult(row: RfpResultRow, input: RfpIndividualResultsInput): RfpCriterionResult {
  const percentage = row.scores.get(input.providerId)?.percentage ?? null;
  const fieldAverage = averageOfOthers(row.scores, input.providerIds, input.providerId);
  return {
    ecNumber: row.ecNumber,
    label: row.label,
    weight: row.weight ?? null,
    percentage,
    fieldAverage,
    deviation: percentage === null || fieldAverage === null ? null : percentage - fieldAverage,
    subCriteria: row.subCriteria
      .map((subRow): RfpSubCriterionResult => ({
        scNumber: subRow.scNumber,
        label: subRow.label,
        criterionLabel: row.label,
        percentage: subRow.scores.get(input.providerId)?.percentage ?? null,
        weight: subRow.weight ?? null,
      }))
      .sort((first, second) => (second.percentage ?? -1) - (first.percentage ?? -1)),
  };
}

function averageOfOthers(
  scores: Map<string, RfpProviderScore>,
  providerIds: string[],
  providerId: string,
): number | null {
  const others = providerIds
    .filter((id) => id !== providerId)
    .map((id) => scores.get(id)?.percentage)
    .filter((percentage): percentage is number => percentage !== undefined);
  return others.length === 0
    ? null
    : others.reduce((sum, percentage) => sum + percentage, 0) / others.length;
}
